import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { LangProvider } from "./i18n";
import { AuthProvider } from "./auth";
import Ticker from "./components/Ticker";
import Navbar from "./components/Navbar";
import Hero from "./components/Hero";
import Services from "./components/Services";
import Calculator from "./components/Calculator";
import Gallery from "./components/Gallery";
import Booking from "./components/Booking";
import Footer, { EditorialMarquee } from "./components/Footer";
import Contact from "./pages/Contact";
import Login from "./pages/Login";
import Portal from "./pages/Portal";
import Team from "./pages/Team";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import AuthCallback from "./components/AuthCallback";
import ChatWidget from "./components/ChatWidget";
import RouteSeo from "./components/Seo";
import SeoPage from "./pages/SeoPage";

function Home() {
  return (
    <>
      <Hero />
      <EditorialMarquee />
      <Services />
      <Calculator />
      <Gallery />
      <Booking />
    </>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function AppShell() {
  const location = useLocation();
  // Google OAuth return: process session_id FIRST (synchronous check, prevents race with auth verification)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <div className="App grain">
      <ScrollToTop />
      <RouteSeo />
      <Ticker />
      <Navbar />
      <main>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/login" element={<Login />} />
          <Route path="/portal" element={<Portal />} />
          <Route path="/team" element={<Team />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/jewelry-casting-italy" element={<SeoPage slug="jewelry-casting-italy" />} />
          <Route path="/lost-wax-casting-jewelry" element={<SeoPage slug="lost-wax-casting-jewelry" />} />
          <Route path="/private-label-jewelry-italy" element={<SeoPage slug="private-label-jewelry-italy" />} />
          {/* Redirects for legacy WordPress URLs */}
          <Route path="/become-our-partner" element={<Navigate to="/contact" replace />} />
          <Route path="/2025/10/13/hello-world" element={<Navigate to="/" replace />} />
          <Route path="/category/uncategorized" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <Footer />
      <Toaster position="bottom-left" />
      <ChatWidget />
    </div>
  );
}

function App() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <LangProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppShell />
        </BrowserRouter>
      </AuthProvider>
    </LangProvider>
  );
}

export default App;
